import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    FleetDashboardAccessItem,
    InsertFleetDashboardAccessRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/fleetDashboardAccess.api.ts';
import {
    createApiFleetDashboardAccess,
    deleteApiFleetDashboardAccess,
    getApiFleetDashboardAccess,
    updateApiFleetDashboardAccess,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/fleetDashboardAccess.api.ts';
import { extractError } from '@utils/slice.ts';

interface FleetDashboardAccessError {
    message?: string;
    [k: string]: any;
}

interface FleetDashboardAccessState {
    error: FleetDashboardAccessError | null;

    fleetDashboardAccessList: FleetDashboardAccessItem[];
    fleetDashboardAccessItem: FleetDashboardAccessItem | null;

    isGetFleetDashboardAccessPending: boolean;
    isCreateFleetDashboardAccessPending: boolean;
    isUpdateFleetDashboardAccessPending: boolean;
    isDeleteFleetDashboardAccessPending: boolean;
}

const initialState: FleetDashboardAccessState = {
    error: null,

    fleetDashboardAccessList: [],
    fleetDashboardAccessItem: null,

    isGetFleetDashboardAccessPending: false,
    isCreateFleetDashboardAccessPending: false,
    isUpdateFleetDashboardAccessPending: false,
    isDeleteFleetDashboardAccessPending: false,
};

export const getFleetDashboardAccess = createAsyncThunk<
    FleetDashboardAccessItem[],
    { tenantAlias: string },
    { rejectValue: FleetDashboardAccessError }
>('fleetDashboardAccess/getFleetDashboardAccess', ({ tenantAlias }, { rejectWithValue }) =>
    getApiFleetDashboardAccess(tenantAlias)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createFleetDashboardAccess = createAsyncThunk<
    FleetDashboardAccessItem,
    { tenantAlias: string; payload: InsertFleetDashboardAccessRequest },
    { rejectValue: FleetDashboardAccessError }
>('fleetDashboardAccess/createFleetDashboardAccess', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiFleetDashboardAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateFleetDashboardAccess = createAsyncThunk<
    FleetDashboardAccessItem,
    { tenantAlias: string; accessId: number; payload: InsertFleetDashboardAccessRequest },
    { rejectValue: FleetDashboardAccessError }
>('fleetDashboardAccess/updateFleetDashboardAccess', ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
    updateApiFleetDashboardAccess(tenantAlias, accessId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteFleetDashboardAccess = createAsyncThunk<
    void,
    { tenantAlias: string; accessId: number },
    { rejectValue: FleetDashboardAccessError }
>('fleetDashboardAccess/deleteFleetDashboardAccess', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiFleetDashboardAccess(tenantAlias, accessId)
        .then(() => undefined)
        .catch((err) => rejectWithValue(err)),
);

const fleetDashboardAccessSlice = createSlice({
    name: 'fleetDashboardAccess',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getFleetDashboardAccess.pending, (state) => {
            state.isGetFleetDashboardAccessPending = true;
            state.error = null;
        });
        builder.addCase(getFleetDashboardAccess.fulfilled, (state, action) => {
            state.isGetFleetDashboardAccessPending = false;
            state.fleetDashboardAccessList = action.payload;
        });
        builder.addCase(getFleetDashboardAccess.rejected, (state, action) => {
            state.isGetFleetDashboardAccessPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createFleetDashboardAccess.pending, (state) => {
            state.isCreateFleetDashboardAccessPending = true;
            state.error = null;
        });
        builder.addCase(createFleetDashboardAccess.fulfilled, (state, action) => {
            state.isCreateFleetDashboardAccessPending = false;
            state.fleetDashboardAccessItem = action.payload;
        });
        builder.addCase(createFleetDashboardAccess.rejected, (state, action) => {
            state.isCreateFleetDashboardAccessPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateFleetDashboardAccess.pending, (state) => {
            state.isUpdateFleetDashboardAccessPending = true;
            state.error = null;
        });
        builder.addCase(updateFleetDashboardAccess.fulfilled, (state, action) => {
            state.isUpdateFleetDashboardAccessPending = false;
            state.fleetDashboardAccessItem = action.payload;
        });
        builder.addCase(updateFleetDashboardAccess.rejected, (state, action) => {
            state.isUpdateFleetDashboardAccessPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteFleetDashboardAccess.pending, (state) => {
            state.isDeleteFleetDashboardAccessPending = true;
            state.error = null;
        });
        builder.addCase(deleteFleetDashboardAccess.fulfilled, (state) => {
            state.isDeleteFleetDashboardAccessPending = false;
        });
        builder.addCase(deleteFleetDashboardAccess.rejected, (state, action) => {
            state.isDeleteFleetDashboardAccessPending = false;
            state.error = extractError(action);
        });
    },
});

export default fleetDashboardAccessSlice.reducer;
